import type { Dispatch, SetStateAction } from "react";
import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";

type Props = Pick<
  KbAdminEditorSectionProps,
  | "articleContent"
  | "setArticleContent"
  | "aiHelpOpen"
  | "setAiHelpOpen"
  | "splitOpen"
  | "setSplitOpen"
  | "aiFollowOnDocs"
  | "aiRecommendedDocCount"
> & {
  showToolGlow: boolean;
  setArticleBodyHadBlurWithText: Dispatch<SetStateAction<boolean>>;
};

export function KbEditorBodyAndPills(props: Props) {
  const {
    articleContent,
    setArticleContent,
    aiHelpOpen,
    setAiHelpOpen,
    splitOpen,
    setSplitOpen,
    aiFollowOnDocs,
    aiRecommendedDocCount,
    showToolGlow,
    setArticleBodyHadBlurWithText,
  } = props;

  const recommendSplit = aiFollowOnDocs.length > 0;
  const splitDocCount = recommendSplit
    ? aiRecommendedDocCount > 1
      ? aiRecommendedDocCount
      : aiFollowOnDocs.length + 1
    : 0;

  return (
    <>
      <div>
        <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Article body</label>
        <p className="mb-2 text-[11px] text-gray-500 leading-relaxed dark:text-zinc-400">
          You write <strong>one KB article</strong> per topic. For search, the app may break this text into <strong>segments</strong> (like chapters)—that does not mean you must author many tiny files. Use{" "}
          <strong>Split into sub-articles</strong> only when you intentionally want separate articles (e.g. warranty vs delivery).
        </p>
        <textarea
          className="h-72 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
          value={articleContent}
          onChange={(event) => setArticleContent(event.target.value)}
          onBlur={(event) => {
            if (event.currentTarget.value.trim()) setArticleBodyHadBlurWithText(true);
          }}
          placeholder="Write the article body in markdown or plain text."
        />
      </div>

      <div className="grid grid-cols-2 gap-3 w-full min-w-0 items-stretch">
        <button
          type="button"
          className={
            "rounded-2xl border px-3 py-1.5 text-left transition-shadow w-full min-w-0 " +
            (aiHelpOpen
              ? "ring-2 ring-amber-400 border-amber-500 bg-amber-50 text-amber-950 shadow-sm dark:ring-amber-500 dark:border-amber-400 dark:bg-amber-950/40 dark:text-amber-100"
              : articleContent.trim() && showToolGlow
                ? "border-amber-400 bg-amber-50 text-amber-950 kb-editor-pill-glow-pulse dark:border-amber-500 dark:bg-amber-950/35 dark:text-amber-100"
                : "border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400 dark:border-amber-600 dark:bg-amber-950/35 dark:text-amber-100 dark:hover:border-amber-500")
          }
          onClick={() => {
            if (aiHelpOpen) setAiHelpOpen(false);
            else {
              setSplitOpen(false);
              setAiHelpOpen(true);
            }
          }}
        >
          <div className="text-xs font-semibold leading-tight text-amber-950 dark:text-amber-100">AI processing</div>
          <div className="text-[10px] font-normal text-amber-800/85 mt-0.5 leading-tight dark:text-amber-200/90">Infer settings & gaps</div>
        </button>

        <button
          type="button"
          className={
            "rounded-2xl border px-3 py-1.5 text-left transition-shadow w-full min-w-0 " +
            (splitOpen
              ? "ring-2 ring-violet-400 border-violet-500 bg-violet-50 text-violet-950 shadow-sm dark:ring-violet-500 dark:border-violet-400 dark:bg-violet-950/40 dark:text-violet-100"
              : articleContent.trim() && showToolGlow
                ? "border-violet-400 bg-violet-50 text-violet-950 kb-editor-pill-glow-pulse dark:border-violet-500 dark:bg-violet-950/35 dark:text-violet-100"
                : "border-violet-300 bg-violet-50 text-violet-900 hover:border-violet-400 dark:border-violet-600 dark:bg-violet-950/35 dark:text-violet-100 dark:hover:border-violet-500")
          }
          onClick={() => {
            if (splitOpen) setSplitOpen(false);
            else {
              setAiHelpOpen(false);
              setSplitOpen(true);
            }
          }}
        >
          <div className="text-xs font-semibold leading-tight text-violet-950 dark:text-violet-100">Split articles</div>
          <div className="text-[10px] font-normal text-violet-800/85 mt-0.5 leading-tight dark:text-violet-200/90">
            {recommendSplit ? `AI suggests ${splitDocCount} articles` : "Optional multi-article"}
          </div>
        </button>
      </div>
    </>
  );
}
